import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
  MatSnackBar
} from '@angular/material';
import { TranslocoService } from '@ngneat/transloco';
import { ApolloError } from 'apollo-client';
import { Logger } from 'aws-amplify';
import { Observable, Subscription } from 'rxjs';
import {
  CreateQuestionnaireRequestInput,
  NameFormat,
  Patient,
  QuestionnaireRequest,
  QuestionnaireType
} from 'src/API';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import {
  QuestionnaireRequestService,
  TabletQuestionnaireModalData
} from 'src/app/core/api/questionnaire-request.service';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { QrCodeModalComponent } from '../qr-code-modal/qr-code-modal.component';
export type TableQuestionnaireMatDialogRef = MatDialogRef<TabletQuestionnaireModalComponent>;

@Component({
  selector: 'csi-table-questionnaire-modal',
  templateUrl: './tablet-questionnaire-modal.component.html',
  styleUrls: ['./tablet-questionnaire-modal.component.css']
})
export class TabletQuestionnaireModalComponent implements OnInit {
  private translationSub: Subscription;
  readonly NameFormat = NameFormat;
  public formGroup: FormGroup;
  public createClicked = false;
  public creatingQuestionnaire = false;
  public questionnaireTypeToFriendlyNameMap = {};
  private logger: Logger = new Logger('SendQuestionnaireDialogComponent');

  constructor(
    public dialogRef: MatDialogRef<TabletQuestionnaireModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TabletQuestionnaireModalData,
    public questionnaireRequestService: QuestionnaireRequestService,
    private dialog: MatDialog,
    private errorHandlerService: ErrorHandlerService,
    private snackBar: MatSnackBar,
    private clinicSetupService: ClinicSetupService,
    public translocoService: TranslocoService
  ) {
    this.formGroup = this.questionnaireRequestService.createTabletQuestionnaireFormGroup(data);
  }

  ngOnInit() {
    const { CUSTOM, ...rest } = this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap;
    this.questionnaireTypeToFriendlyNameMap = rest;
  }

  ngOnDestroy() {
    if (this.translationSub) {
      this.translationSub.unsubscribe();
    }
  }

  public static open(
    matDialogService: MatDialog,
    questionnaireType: QuestionnaireType,
    patient: Partial<Patient>,
    modalConfig: MatDialogConfig<never> = {}
  ): TableQuestionnaireMatDialogRef {
    return matDialogService.open<TabletQuestionnaireModalComponent, TabletQuestionnaireModalData>(
      TabletQuestionnaireModalComponent,
      {
        maxWidth: '95vw',
        height: 'auto',
        ...modalConfig,
        data: {
          patient,
          questionnaireType
        }
      }
    );
  }

  onSubmit() {
    this.createClicked = true;
    if (this.formGroup.invalid) return;

    this.creatingQuestionnaire = true;
    if (!this.data.questionnaireRequest) {
      this.createQuestionnaireRequest();
      return;
    }

    this.updateQuestionnaireType();
  }

  noSort() {
    return 0;
  }

  openQrCodeModal(questionnaireId: string) {
    const path = `${location.origin}/questionnaires/${questionnaireId}`;
    const questionnaireType: string = this.questionnaireRequestService
      .questionnaireTypeToFriendlyNameMap[this.formGroup.controls.type.value];

    this.dialog.open(QrCodeModalComponent, {
      data: {
        qrCodePath: path,
        modalTitle: `Scan the QR Code to open the ${questionnaireType} questionnaire`
      }
    });
  }

  private createQuestionnaireRequest() {
    const questionnaireType = this.formGroup.controls.type.value;
    const questionnaireRequestInput: CreateQuestionnaireRequestInput = {
      ...this.formGroup.value,
      config: {
        questionnaireExpiry: this.clinicSetupService.clinicSetupFormGroup.value.questionnaireExpiry
      }
    };

    const schema = this.questionnaireRequestService.allQuestionnairesAndIntakeFormsKeyToSchemaMap[
      questionnaireType
    ];

    const clinic = this.clinicSetupService.clinic;
    if (clinic) {
      (questionnaireRequestInput.config as any).clinicBranding = {
        name: clinic.name,
        logo: clinic.logo || null
      };
    }

    if (schema) {
      questionnaireRequestInput.type = this.questionnaireRequestService
        .formTemplateLibraryQuestionnaires[questionnaireType]
        ? (questionnaireType as QuestionnaireType)
        : QuestionnaireType.CUSTOM;

      questionnaireRequestInput.config = {
        ...(questionnaireRequestInput.config as any),
        schema: schema
      };

      if (schema.type === 'consent') {
        questionnaireRequestInput.language = 'en';
      }
    }

    questionnaireRequestInput.config = JSON.stringify(questionnaireRequestInput.config as any);
    let questionnaireRequest$: Observable<QuestionnaireRequest>;

    questionnaireRequest$ = this.questionnaireRequestService.createQuestionnaireRequest(
      questionnaireRequestInput
    );

    questionnaireRequest$.subscribe(
      (questionnaireRequest: QuestionnaireRequest) => {
        this.logger.debug('Submitted Questionnaire Request: ', questionnaireRequest);
        this.creatingQuestionnaire = false;
        this.openQrCodeModal(questionnaireRequest.id);
        this.dialogRef.close(questionnaireRequest);
        this.openSuccessSnackbar();
      },
      (error: ApolloError) => {
        this.creatingQuestionnaire = false;
        this.errorHandlerService.handleGraphQLError(error);
      }
    );
  }

  public updateQuestionnaireType() {
    this.questionnaireRequestService
      .updateQuestionnaireType(this.data.questionnaireRequest, this.formGroup.controls.type.value)
      .subscribe(
        (questionnaireRequest: QuestionnaireRequest) => {
          this.logger.debug('Submitted Questionnaire Request: ', questionnaireRequest);
          this.creatingQuestionnaire = false;
          this.openQrCodeModal(questionnaireRequest.id);
          this.dialogRef.close(questionnaireRequest);
          this.openSuccessSnackbar();
        },
        (error: ApolloError) => {
          this.creatingQuestionnaire = false;
          this.errorHandlerService.handleGraphQLError(error);
        }
      );
  }

  private openSuccessSnackbar() {
    const questionnaireType: string = this.questionnaireRequestService
      .questionnaireTypeToFriendlyNameMap[this.formGroup.controls.type.value];

    let dismissText = 'Dismiss';
    this.translationSub = this.translocoService.selectTranslate('Dismiss').subscribe(text => {
      dismissText = text || dismissText;
    });

    let questionnaireGenerated = 'Questionnaire Generated';
    this.translationSub = this.translocoService
      .selectTranslate('Questionnaire Generated')
      .subscribe(text => {
        questionnaireGenerated = text || questionnaireGenerated;
      });

    this.snackBar.open(`${questionnaireType} ${questionnaireGenerated}!`, dismissText, {
      duration: 2000
    });
  }
}
